/**
 * Phase 2R-Freeze — Realtime Ops Dashboard (super_admin only).
 *
 * Calm, dark, diagnostic.  Polls /api/arenax/realtime/telemetry every 5s,
 * keeps a small in-memory rolling history for the sparklines, and never
 * persists.  ArenaX aesthetic persists internally — no DevOps noise.
 *
 * CEO contract:
 *   - super_admin only
 *   - REALTIME FREEZE ACTIVE banner when telemetry.freeze_mode === true
 *   - 4 telemetry cards: Replay Integrity / Fanout Health / Cleanup Pressure / Cross-Pod Drift
 *   - System state pills with HUMAN wording
 *   - Lightweight sparklines (not big charts)
 *   - Narrative anomaly labels, never technical deltas
 */
import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Activity, Radio, Layers, Wind, Snowflake } from 'lucide-react';

const POLL_MS = 5000;
const HISTORY_LEN = 30; // 30 polls × 5s = 2.5 minutes of operational sight

function Sparkline({ values = [], color = '#06b6d4', height = 28, width = 96 }) {
  if (!values.length) {
    return <div style={{ height, width }} className="opacity-30 text-[10px] text-muted-foreground">—</div>;
  }
  const max = Math.max(1, ...values);
  const min = Math.min(0, ...values);
  const range = max - min || 1;
  const step = width / Math.max(1, values.length - 1);
  const path = values
    .map((v, i) => {
      const x = i * step;
      const y = height - ((v - min) / range) * height;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={width} height={height} className="opacity-90" data-testid="arenax-ops-sparkline">
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function PillState({ state }) {
  const styles = {
    ok: { bg: 'rgba(16,185,129,0.10)', dot: '#10b981', text: '#10b981' },
    warn: { bg: 'rgba(245,158,11,0.10)', dot: '#f59e0b', text: '#f59e0b' },
    info: { bg: 'rgba(6,182,212,0.10)', dot: '#06b6d4', text: '#06b6d4' },
  }[state || 'ok'];
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider"
      style={{ color: styles.text }}
    >
      <span
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={{ background: styles.dot, boxShadow: state === 'warn' ? `0 0 12px ${styles.dot}` : 'none' }}
      />
    </span>
  );
}

function TelemetryCard({ icon: Icon, title, value, suffix, history, color, anomaly, testid }) {
  return (
    <Card
      className="overflow-hidden border-0"
      style={{
        background: 'var(--ax-card-bg-soft)',
        backdropFilter: 'blur(12px)',
        boxShadow: anomaly ? `inset 0 0 0 1px ${color}` : 'inset 0 0 0 1px rgba(148,163,184,0.10)',
        transition: 'box-shadow 600ms ease',
      }}
      data-testid={testid}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-slate-400">
            <Icon className="h-3.5 w-3.5" style={{ color }} />
            {title}
          </div>
          {anomaly && <PillState state="warn" />}
        </div>
        <div className="flex items-end justify-between">
          <div className="flex items-baseline gap-1.5">
            <span className="text-3xl font-light tabular-nums" style={{ color: anomaly ? color : '#e2e8f0' }}>
              {value}
            </span>
            {suffix && <span className="text-[11px] text-slate-500">{suffix}</span>}
          </div>
          <Sparkline values={history} color={color} />
        </div>
      </CardContent>
    </Card>
  );
}

export default function ArenaxOps() {
  const [snap, setSnap] = useState(null);
  const [error, setError] = useState(null);
  const histRef = useRef({
    replay_hit_rate: [],
    fanout_p99: [],
    dead_sockets: [],
    cross_pod: [],
    broadcasts: [],
    duplicates: [],
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let stopped = false;
    const poll = async () => {
      try {
        const r = await api.get('/arenax/realtime/telemetry');
        if (stopped) return;
        const s = r.data;
        const oneMin = s.windows?.['1m'] || {};
        const reqs = oneMin.replay_request_total || 0;
        const hitRate = reqs > 0 ? (oneMin.replay_served / reqs) * 100 : 100;
        const h = histRef.current;
        const push = (arr, v) => {
          arr.push(v);
          if (arr.length > HISTORY_LEN) arr.shift();
        };
        push(h.replay_hit_rate, hitRate);
        push(h.fanout_p99, s.fanout_latency_ms?.p99 || 0);
        push(h.dead_sockets, oneMin.dead_sockets_cleaned || 0);
        push(h.cross_pod, oneMin.cross_pod_received || 0);
        push(h.broadcasts, oneMin.broadcasts_total || 0);
        push(h.duplicates, oneMin.duplicates_suppressed || 0);
        setSnap(s);
        setTick(t => t + 1);
        setError(null);
      } catch (e) {
        setError(e?.response?.status === 403 ? 'Super admin access required.' : 'Telemetry temporarily unavailable.');
      }
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, []);

  const oneMin = snap?.windows?.['1m'] || {};
  const lat = snap?.fanout_latency_ms || {};
  const flags = snap?.anomaly_flags || {};
  const reqs1m = oneMin.replay_request_total || 0;
  const hit = reqs1m > 0 ? Math.round((oneMin.replay_served / reqs1m) * 100) : 100;
  const crossPodOff = !snap?.redis_enabled;

  return (
    <div
      className="min-h-screen text-slate-200"
      style={{
        background:
          'radial-gradient(1200px 600px at 20% -10%, rgba(6,182,212,0.12), transparent 60%), radial-gradient(1000px 500px at 90% 110%, rgba(245,158,11,0.08), transparent 60%), #020617',
      }}
      data-testid="arenax-ops-root"
    >
      <div className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        {/* Header */}
        <div className="flex items-end justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">ArenaX · Realtime Operations</div>
            <h1
              className="text-3xl font-light mt-2 tracking-tight"
              style={{ color: '#e2e8f0' }}
              data-testid="arenax-ops-title"
            >
              Watching the nervous system.
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Polling every 5 seconds. The dashboard does not retain history beyond your session.
            </p>
          </div>
          {snap?.freeze_mode && (
            <div
              className="px-3 py-1.5 text-[11px] uppercase tracking-[0.25em] flex items-center gap-2 self-start"
              style={{
                background: 'rgba(6,182,212,0.10)',
                color: '#7dd3fc',
                border: '1px solid rgba(6,182,212,0.30)',
                borderRadius: '4px',
              }}
              data-testid="arenax-ops-freeze-banner"
            >
              <Snowflake className="h-3.5 w-3.5" />
              Realtime Freeze Active
            </div>
          )}
        </div>

        {/* System pills */}
        {snap?.pills && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="arenax-ops-pills">
            {snap.pills.map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-[12px]" data-testid={`arenax-ops-pill-${p.kind}`}>
                <PillState state={p.state} />
                <span className="text-slate-300">{p.label}</span>
              </div>
            ))}
            <div className="ml-auto text-[10px] text-slate-600 tabular-nums">
              uptime {snap.uptime_sec}s · tick {tick}
            </div>
          </div>
        )}

        {/* 4 telemetry cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <TelemetryCard
            icon={Radio}
            title="Replay Integrity"
            value={hit}
            suffix="% hit · 1m"
            history={histRef.current.replay_hit_rate}
            color="#06b6d4"
            anomaly={oneMin.replay_miss > 0 && hit < 95}
            testid="arenax-ops-card-replay"
          />
          <TelemetryCard
            icon={Wind}
            title="Fanout Health"
            value={Math.round(lat.p99 || 0)}
            suffix={`ms p99 · ${lat.samples || 0} obs`}
            history={histRef.current.fanout_p99}
            color="#f59e0b"
            anomaly={!!flags.fanout_latency_degraded}
            testid="arenax-ops-card-fanout"
          />
          <TelemetryCard
            icon={Layers}
            title="Cleanup Pressure"
            value={oneMin.dead_sockets_cleaned || 0}
            suffix="sockets · 1m"
            history={histRef.current.dead_sockets}
            color="#ef4444"
            anomaly={!!flags.cleanup_pressure_high}
            testid="arenax-ops-card-cleanup"
          />
          <TelemetryCard
            icon={Activity}
            title="Cross-Pod Drift"
            value={crossPodOff ? '—' : oneMin.cross_pod_received || 0}
            suffix={crossPodOff ? 'single pod' : 'events · 1m'}
            history={histRef.current.cross_pod}
            color="#a855f7"
            anomaly={false}
            testid="arenax-ops-card-cross-pod"
          />
        </div>

        {/* Narrative anomalies — calm, ArenaX-toned */}
        {snap?.narrative_anomalies?.length > 0 && (
          <div className="space-y-1" data-testid="arenax-ops-narrative">
            {snap.narrative_anomalies.map((n, i) => (
              <div
                key={i}
                className="text-[13px] tracking-wide"
                style={{ color: n.level === 'warn' ? '#fbbf24' : '#94a3b8' }}
              >
                <span className="mr-2 opacity-60">›</span>
                {n.label}
              </div>
            ))}
          </div>
        )}

        {/* Quiet stat row + per-channel-type */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
          <QuietStat label="Broadcasts · 1m" value={oneMin.broadcasts_total || 0} hist={histRef.current.broadcasts} />
          <QuietStat label="Duplicates · 1m" value={oneMin.duplicates_suppressed || 0} hist={histRef.current.duplicates} />
          <QuietStat label="Replay served · life" value={snap?.replay?.served_from_buffer || 0} />
          <QuietStat label="Replay evictions" value={snap?.replay?.evictions || 0} />
        </div>

        {snap?.by_channel_type && Object.keys(snap.by_channel_type).length > 0 && (
          <div className="space-y-2" data-testid="arenax-ops-by-channel-type">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">By channel type · last minute</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {Object.entries(snap.by_channel_type).map(([k, v]) => (
                <div
                  key={k}
                  className="px-3 py-2 text-[12px] tabular-nums"
                  style={{
                    background: 'var(--ax-card-bg-soft)',
                    border: '1px solid rgba(148,163,184,0.08)',
                    borderRadius: '4px',
                  }}
                >
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
                  <div className="text-slate-200">{v.broadcasts_total} broadcasts</div>
                  <div className="text-slate-500 text-[11px]">
                    {v.duplicates_suppressed} dupes · {v.dead_sockets_cleaned} dead
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="text-[12px] text-rose-400" data-testid="arenax-ops-error">
            {error}
          </div>
        )}

        <div className="pt-8 text-[10px] text-slate-700 tracking-wider">
          ArenaX · operational consciousness layer · v2R-freeze
        </div>
      </div>
    </div>
  );
}

function QuietStat({ label, value, hist }) {
  return (
    <div
      className="px-3 py-2.5"
      style={{
        background: 'var(--ax-card-bg-soft)',
        border: '1px solid rgba(148,163,184,0.06)',
        borderRadius: '4px',
      }}
    >
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="flex items-baseline justify-between mt-1">
        <span className="text-xl font-light text-slate-300 tabular-nums">{value}</span>
        {hist && <Sparkline values={hist} color="#475569" height={18} width={56} />}
      </div>
    </div>
  );
}
